import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  service: z.string().min(1, 'Please select a service'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
    },
  });

  const services = [
    'Private Limited Registration',
    'LLP Registration',
    'OPC Registration',
    'GST Registration',
    'GST Return Filing',
    'Trademark Registration',
    'FSSAI License',
    'Income Tax Filing',
    'Company Compliance',
    'Other Services',
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, data);
      setIsSubmitted(true);
      toast.success('Thank you! We will contact you shortly.');
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 98206 69943',
      link: 'tel:+919820669943',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'info@gklegalsolutions.com',
      link: 'mailto:info@gklegalsolutions.com',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      content: 'Mon - Sat: 10:30 AM - 7:00 PM',
      link: null,
    },
    {
      icon: MapPin,
      title: 'Office',
      content: 'Mumbai, Maharashtra, India',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <Helmet>
        <title>Contact Us - GK Legal Solutions | Get Free Consultation</title>
        <meta name="description" content="Contact GK Legal Solutions for Company Registration, GST, Trademark & Compliance services. Call +91 98206 69943 or fill our contact form for free consultation." />
        <link rel="canonical" href="https://gklegalsolutions.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Contact Us</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-4">
              Get in Touch
            </h1>
            <p className="font-body text-lg text-slate-600">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <p className="font-body text-slate-600 mb-8">
                Reach out to us for any queries about our services. Our team is ready to assist you.
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-slate-500 mb-1">{info.title}</p>
                      {info.link ? (
                        <a href={info.link} className="font-body font-medium text-slate-900 hover:text-primary transition-colors">
                          {info.content}
                        </a>
                      ) : (
                        <p className="font-body font-medium text-slate-900">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map Placeholder */}
              <div className="mt-10 rounded-xl overflow-hidden border border-slate-200">
                <div className="bg-slate-100 h-64 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-primary mx-auto mb-3" />
                    <p className="font-body text-slate-600">Mumbai, Maharashtra</p>
                    <p className="font-body text-sm text-slate-500">India</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-slate-50 p-8 md:p-10 rounded-2xl">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Send us a Message</h2>
                <p className="font-body text-slate-600 mb-8">Fill out the form below and we'll get back to you shortly.</p>

                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Thank You!</h3>
                    <p className="font-body text-slate-600">We have received your message and will contact you shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="font-body text-sm font-medium text-slate-700 mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          {...register('name')}
                          placeholder="John Doe"
                          className={errors.name ? 'border-red-500' : ''}
                          data-testid="contact-name-input"
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-body text-sm font-medium text-slate-700 mb-2 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          {...register('email')}
                          placeholder="john@example.com"
                          className={errors.email ? 'border-red-500' : ''}
                          data-testid="contact-email-input"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="font-body text-sm font-medium text-slate-700 mb-2 block">
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          {...register('phone')}
                          placeholder="+91 98765 43210"
                          className={errors.phone ? 'border-red-500' : ''}
                          data-testid="contact-phone-input"
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="service" className="font-body text-sm font-medium text-slate-700 mb-2 block">
                          Service Interested *
                        </Label>
                        <Select onValueChange={(value) => setValue('service', value)}>
                          <SelectTrigger className={errors.service ? 'border-red-500' : ''} data-testid="contact-service-select">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.service && (
                          <p className="text-red-500 text-sm mt-1">{errors.service.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-body text-sm font-medium text-slate-700 mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        {...register('message')}
                        placeholder="Tell us about your requirements..."
                        rows={5}
                        className={errors.message ? 'border-red-500' : ''}
                        data-testid="contact-message-input"
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Additional Info */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Prefer to Talk?
            </h2>
            <p className="font-body text-lg text-slate-600 mb-8">
              Call us directly or send a message on WhatsApp for instant support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+919820669943">
                <Button className="btn-primary flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call Now
                </Button>
              </a>
              <a href="https://wa.me/919820669943" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="btn-secondary flex items-center gap-2">
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
